/**
 * @file PlanOverviewTab.js
 * @description Tab component for managing basic plan information.
 */

import { useState, useEffect } from 'react';
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import CustomDatePicker from '../../helpers/CustomDatePicker_V2';
import { searchPlanYears } from '../../../services/planYearService';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import { getText } from '../../../i18n';

const PlanOverviewTab = ({ data, onChange, onSave }) => {
    const [formData, setFormData] = useState({
        code: data.code || '',
        name: data.name || '',
        startDate: data.startDate || null,
        endDate: data.endDate || null,
        isOffCycle: data.isOffCycle || false,
    });

    const [errors, setErrors] = useState({
        code: '',
        name: '',
        startDate: '',
        endDate: '',
    });

    const [isDefaultStartDateSet, setIsDefaultStartDateSet] = useState(false);
    const [isLoadingDefaultStartDate, setIsLoadingDefaultStartDate] =
        useState(false);

    useEffect(() => {
        setFormData({
            code: data.code || '',
            name: data.name || '',
            startDate: data.startDate || null,
            endDate: data.endDate || null,
            isOffCycle: data.isOffCycle || false,
        });
    }, [data]);

    useEffect(() => {
        const setDefaultStartDate = async () => {
            if (
                !data.startDate &&
                (!data.id || data.id === 0) &&
                !isDefaultStartDateSet
            ) {
                setIsLoadingDefaultStartDate(true);
                try {
                    const allPlanYearsResponse = await searchPlanYears(
                        null,
                        1,
                        1,
                        '',
                        'endDate',
                        'desc',
                    );

                    const allPlanYears = allPlanYearsResponse.viewModels || [];

                    const latestEndDate =
                        allPlanYears.length > 0 && allPlanYears[0].endDate
                            ? new Date(allPlanYears[0].endDate)
                            : null;

                    if (latestEndDate) {
                        const defaultStartDate = new Date(latestEndDate);
                        defaultStartDate.setDate(
                            defaultStartDate.getDate() + 1,
                        );
                        const defaultStartDateString = defaultStartDate
                            .toISOString()
                            .split('T')[0];

                        setFormData(prevData => ({
                            ...prevData,
                            startDate: defaultStartDateString,
                        }));

                        setIsDefaultStartDateSet(true);
                    }
                } catch (error) {
                    console.error(
                        'Failed to fetch plan years for default start date',
                        error,
                    );
                } finally {
                    setIsLoadingDefaultStartDate(false);
                }
            }
        };

        setDefaultStartDate();
    }, [data.id, data.startDate, onChange, isDefaultStartDateSet]);

    const validateField = (name, value) => {
        let errorMessage = '';

        switch (name) {
            case 'code':
                if (!value.trim()) {
                    errorMessage = 'Plan year code is required';
                } else if (value.length > 50) {
                    errorMessage = 'Plan year code cannot exceed 50 characters';
                }
                break;

            case 'name':
                if (!value.trim()) {
                    errorMessage = 'Plan year name is required';
                } else if (value.length > 100) {
                    errorMessage =
                        'Plan year name cannot exceed 100 characters';
                }
                break;

            case 'startDate':
                if (!value) {
                    errorMessage = 'Start date is required';
                }
                break;

            case 'endDate':
                if (!value) {
                    errorMessage = 'End date is required';
                } else if (
                    formData.startDate &&
                    new Date(value) < new Date(formData.startDate)
                ) {
                    errorMessage = 'End date must be after start date';
                }
                break;

            default:
                break;
        }

        return errorMessage;
    };

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        const errorMessage = validateField(name, fieldValue);

        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: errorMessage,
        }));

        setFormData(prevData => {
            const newData = {
                ...prevData,
                [name]: fieldValue,
            };
            onChange(newData);
            return newData;
        });
    };

    const handleDateChange = (field, date) => {
        const errorMessage = validateField(field, date);

        setErrors(prevErrors => ({
            ...prevErrors,
            [field]: errorMessage,
        }));

        setFormData(prevData => {
            const newData = {
                ...prevData,
                [field]: date,
            };
            onChange(newData);
            return newData;
        });

        if (field === 'startDate' && formData.endDate) {
            const endDateError =
                date && new Date(formData.endDate) < new Date(date)
                    ? 'End date must be after start date'
                    : '';

            setErrors(prevErrors => ({
                ...prevErrors,
                endDate: endDateError,
            }));
        }
    };

    return (
        <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto pb-5 pt-4">
            <div className="form-group mb-3">
                <label htmlFor="code">
                    {getText('msg_9481543603db')} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                    <input
                        type="text"
                        id="code"
                        name="code"
                        className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                        value={formData.code}
                        onChange={handleChange}
                        required
                        placeholder={getText('msg_f862e4bfb473')}
                        maxLength={50}
                    />
                    <CharacterLimitIndicator
                        inputStr={formData.code}
                        maxChars={50}
                    />
                    {errors.code && (
                        <div className="invalid-feedback">{errors.code}</div>
                    )}
                </div>
            </div>

            <div className="form-group mb-3">
                <label htmlFor="name">
                    {getText('msg_637d149f9e6f')} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={getText('msg_82559f0ca41d')}
                        maxLength={100}
                    />
                    <CharacterLimitIndicator
                        inputStr={formData.name}
                        maxChars={100}
                    />
                    {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                    )}
                </div>
            </div>

            <div className="form-group mb-3">
                <label htmlFor="startDate">
                    {getText('msg_9d7ab1a55796')} <span className="text-danger">*</span>
                </label>
                <LoadingWrapper isLoading={isLoadingDefaultStartDate}>
                    <CustomDatePicker
                        selectedDate={formData.startDate}
                        onDateChange={date =>
                            handleDateChange('startDate', date)
                        }
                        errorMessage={errors.startDate}
                    />
                </LoadingWrapper>
            </div>

            <div className="form-group mb-3">
                <label htmlFor="endDate">
                    {getText('msg_84b147812589')} <span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                    selectedDate={formData.endDate}
                    onDateChange={date => handleDateChange('endDate', date)}
                    errorMessage={errors.endDate}
                    minDate={
                        formData.startDate ? new Date(formData.startDate) : null
                    }
                />
            </div>

            <div className="form-group mb-3">
                <label htmlFor="isOffCycle" className="form-check-label pe-3">
                    {getText('msg_fd575ede4748')}
                </label>
                <input
                    type="checkbox"
                    id="isOffCycle"
                    name="isOffCycle"
                    className="form-check-input"
                    checked={formData.isOffCycle}
                    onChange={handleChange}
                />
            </div>

            <div className="d-flex justify-content-between mt-4 pt-4 col-12 m-auto">
                <p>
                    <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                </p>
                <button
                    type="button"
                    className="button-base ing-button-primary-left"
                    onClick={onSave}
                    disabled={
                        !formData.code ||
                        !formData.name ||
                        !formData.startDate ||
                        !formData.endDate ||
                        errors.code ||
                        errors.name ||
                        errors.startDate ||
                        errors.endDate
                    }
                >
                    {getText('msg_efc007a393f6')}
                </button>
            </div>
        </div>
    );
};

export default PlanOverviewTab;
